import React, { useRef, useState } from 'react';
import Button from '~/components/Button';
import EventToolbar from '~/components/EventToolbar';
import InputField from '~/components/InputField';
import ModalDatePicker, {
  ModalDatePickerHandler,
} from '~/components/ModalDatePicker';
import ModalTimePicker, {
  ModalTimePickerHandler,
} from '~/components/ModalTimePicker';
import {
  SettingPanel,
  SettingToggleItem,
  SettingLabelItem,
} from '~/components/SettingPanel';
import SwitchSelector from '~/components/SwitchSelector';
import { Spacer } from '~/components/common/style.web';
import { CalendarDateObject } from '~/data/models/calendar';
import { GroupUser, UserGroup } from '~/data/models/group';
import { Team } from '~/data/models/team';
import { ModuleInstance } from '~/data/models/yearbook';
import { EventType } from '~/data/types/graphql';
import { CreateEventInput } from '~/data/types/graphql';
import { ModalScreenLayout } from '~/screens/style';
import {
  calendarDateObject,
  combineDateAndTime,
  formattedDate,
} from '~/utils/dates';
import { t } from '~/utils/i18n';
import { getImageFromLibrary } from '~/utils/image-picker';
import {
  ScrollView,
  Title,
  ImageContent,
  EmptyImageIcon,
  ImageBanner,
  AboutEvent,
  CreateEventContainer,
} from './style';

type InviteOption = 'classmate' | 'class' | 'team';
type AuthorType = 'GROUP' | 'USER' | 'TEAM';

type CreateEventLayoutProps = {
  teams: Team[];
  groupsUser: GroupUser[];
  loggedUserGroup?: UserGroup | null;
  loading: boolean;
  initialName?: string;
  initialLocation?: string;
  initialTime?: Date;
  initialDescription?: string;
  initialInviteOption?: InviteOption;
  moduleInstance?: ModuleInstance;
  onBack: () => void;
  onCreateEvent: (input: CreateEventInput) => void;
  authUserId: string;
};

export default function CreateEvent({
  teams,
  groupsUser,
  initialName = '',
  initialLocation = '',
  initialDescription = '',
  loggedUserGroup,
  moduleInstance,
  onBack,
  onCreateEvent,
  loading,
}: CreateEventLayoutProps): JSX.Element {
  const startModalizeRef = useRef<ModalDatePickerHandler>(null);
  const startTimeModalizeRef = useRef<ModalTimePickerHandler>(null);
  const endModalizeRef = useRef<ModalDatePickerHandler>(null);
  const endTimeModalizeRef = useRef<ModalTimePickerHandler>(null);

  const [eventKey, setEventKey] = useState<string>(EventType.EVENT);

  const [name, setName] = useState<string>(initialName);
  const [location, setLocation] = useState<string>(initialLocation);
  const [about, setAbout] = useState<string>(initialDescription);
  const [authorId, setAuthorId] = useState<string | undefined>();
  const [authorType, setAuthorType] = useState<AuthorType>('USER');
  const [imgUri, setImgUri] = useState<string>('');
  const [isJoinable, setIsJoinable] = useState(false);
  const [isPublic, setIsPublic] = useState(false);

  const getInitialDueDay = () =>
    moduleInstance?.dueDate
      ? calendarDateObject(formattedDate(moduleInstance?.dueDate, 'yyyy-MM-dd'))
      : null;

  const getInitialDueTime = () =>
    moduleInstance?.dueDate
      ? new Date(Date.parse(moduleInstance?.dueDate))
      : undefined;

  const [isFullDayEvent, setIsFullDayEvent] = useState(false);

  const [startTimeDate, setStartTimeDate] = useState<Date | undefined>(
    getInitialDueTime(),
  );
  const [endTimeDate, setEndTimeDate] = useState<Date | undefined>(
    getInitialDueTime(),
  );
  const [startDay, setStartDay] = useState<CalendarDateObject | null>(
    getInitialDueDay(),
  );
  const [endDay, setEndDay] = useState<CalendarDateObject | null>(
    getInitialDueDay(),
  );

  const onPickImage = async () => {
    const imgUri = await getImageFromLibrary({ maxFiles: 1 });
    setImgUri(imgUri?.[0]);
  };

  const onSubmit = () => {
    // Prepare datetime data for request
    const timeFormat = 'h:mm A';
    const [startTime, endTime] = [
      formattedDate(startTimeDate || '', timeFormat),
      formattedDate(endTimeDate || '', timeFormat),
    ];
    const [startDateTime, endDateTime] = [
      combineDateAndTime(startDay?.dateString, startTime),
      combineDateAndTime(endDay?.dateString, endTime),
    ];

    const input: CreateEventInput = {
      title: name,
      bannerImage: imgUri,
      startDateTime,
      endDateTime,
      isAllDay: isFullDayEvent,
      eventType: eventKey.toUpperCase(),
      authorType: authorType || 'USER',
      authorUser: authorType === 'USER' ? authorId : null,
      authorGroup: authorType === 'GROUP' ? authorId : null,
      authorTeam: authorType === 'TEAM' ? authorId : null,
      location: location,
      description: about,
      isJoinable: isJoinable,
      isPublic: isPublic,
    };

    onCreateEvent(input);
  };
  return (
    <ModalScreenLayout
      backButtonTestID={'buttonBack'}
      title={t('screens.createEvent')}
      onBackPress={onBack}
    >
      <ScrollView showsVerticalScrollIndicator={false}>
        <Title>{t('createEvent.title')}</Title>
        <InputField
          testID={'inputName'}
          value={name}
          label={t('createEvent.nameLabel')}
          placeholder={t('createEvent.namePlaceholder')}
          onChangeText={(value) => setName(value)}
        />
        <Title>{t('g.createEventBanner')}</Title>
        <ImageContent onPress={onPickImage}>
          <ImageBanner
            testID={`PagePhotoCategoryCardImage`}
            source={{ uri: imgUri }}
          >
            {!imgUri && <EmptyImageIcon />}
          </ImageBanner>
        </ImageContent>
        <Title>{t('createEvent.dateTitle')}</Title>
        <SettingPanel>
          <SettingToggleItem
            testID={'isActivateToggleItem'}
            text={t('g.allDayEvent')}
            checked={isFullDayEvent as boolean}
            onToggleChange={setIsFullDayEvent}
          />
          <SettingLabelItem
            testID={'buttonStartDate'}
            text={t('g.startDay')}
            labelText={
              startDay ? formattedDate(startDay.dateString, 'MMM d yyyy') : ''
            }
            onLabelPress={() => startModalizeRef.current?.open()}
          />
          {!isFullDayEvent && (
            <SettingLabelItem
              testID={'buttonDueTime'}
              text={t('g.dueTime')}
              labelText={
                startTimeDate ? formattedDate(startTimeDate, 'hh:mm a') : ''
              }
              onLabelPress={() => startTimeModalizeRef.current?.open()}
            />
          )}

          <SettingLabelItem
            testID={'buttonEndDate'}
            text={t('g.endDay')}
            labelText={
              endDay ? formattedDate(endDay.dateString, 'MMM d yyyy') : ''
            }
            onLabelPress={() => endModalizeRef.current?.open()}
          />
          {!isFullDayEvent && (
            <SettingLabelItem
              testID={'buttonDueTime'}
              text={t('g.dueTime')}
              labelText={
                endTimeDate ? formattedDate(endTimeDate, 'hh:mm a') : ''
              }
              onLabelPress={() => endTimeModalizeRef.current?.open()}
            />
          )}
        </SettingPanel>
        <Title>{t('createEvent.typeTitle')}</Title>
        <SwitchSelector
          items={Object.keys(EventType).map((key) => ({
            key,
            title: key.charAt(0) + key.substr(1).toLowerCase(),
          }))}
          selectedItemKey={eventKey}
          onSelectedItem={setEventKey}
          testID="pickerEventType"
        />
        <Title>{t('createEvent.locationTitle')}</Title>
        <InputField
          testID={'inputLocation'}
          placeholder={t('createEvent.namePlaceholder')}
          onChangeText={(value) => setLocation(value)}
          value={location}
        />
        <Title>{t('createEvent.aboutEventTitle')}</Title>
        <AboutEvent
          testID={'inputAbout'}
          multiline
          placeholder={t('createEvent.describeEvent')}
          style={{ textAlignVertical: 'top' }}
          onChangeText={(value) => setAbout(value)}
          value={about}
        />

        <>
          <Title>{t('createEvent.postAs')}</Title>
          <EventToolbar
            testID={loggedUserGroup ? 'toolbarInput' : ''}
            author={authorId}
            userGroup={loggedUserGroup}
            teams={teams}
            groups={groupsUser}
            onAuthorChange={(authorId, type) => {
              setAuthorId(authorId);
              setAuthorType(type as AuthorType);
            }}
          />
          <Title>{t('createEvent.settings')}</Title>
          <SettingPanel>
            <SettingToggleItem
              testID={'isActivateToggleItem'}
              text={t('createEvent.joinable')}
              checked={isJoinable as boolean}
              onToggleChange={setIsJoinable}
            />
            <Spacer v={8} />
            <SettingToggleItem
              testID={'isActivateToggleItem'}
              text={t('createEvent.public')}
              checked={isPublic as boolean}
              onToggleChange={setIsPublic}
            />
          </SettingPanel>
        </>
        <Spacer v={300} />
      </ScrollView>

      <CreateEventContainer>
        <Button
          testID={'buttonCreateEvent'}
          text={t('createEvent.createEventButton')}
          size={'lg'}
          type={'primary-brand-01'}
          iconName={'plus'}
          onPress={onSubmit}
          flex
          loading={loading}
        />
      </CreateEventContainer>

      {/* MODALS */}
      <ModalDatePicker
        testID={'startDatePicker'}
        ref={startModalizeRef}
        title={t('g.startDay')}
        buttonText={t('g.confirmDate')}
        date={startDay}
        minDate={new Date().toDateString()}
        mode={'single'}
        onConfirmDate={(dateObj) => {
          setStartDay(dateObj);
          startModalizeRef.current?.close();
        }}
      />

      <ModalDatePicker
        testID={'endDatePicker'}
        ref={endModalizeRef}
        title={t('g.endDay')}
        buttonText={t('g.confirmDate')}
        date={endDay}
        minDate={startDay ? startDay.dateString : new Date().toDateString()}
        maxDate={endDay ? endDay.dateString : undefined}
        mode={'single'}
        onConfirmDate={(dateObj) => {
          setEndDay(dateObj);
          endModalizeRef.current?.close();
        }}
      />

      <ModalTimePicker
        testID={'endTimePicker'}
        ref={endTimeModalizeRef}
        title={t('g.deadlineTime')}
        buttonText={t('g.confirmTime')}
        time={endTimeDate}
        onConfirmTime={(time) => {
          setEndTimeDate(time);
          endTimeModalizeRef.current?.close();
        }}
      />

      <ModalTimePicker
        testID={'startTimePicker'}
        ref={startTimeModalizeRef}
        title={t('createEvent.timeModalTitle')}
        buttonText={t('createEvent.timeModalButtonText')}
        time={
          startTimeDate ? new Date(`1970-01-01T${startTimeDate}:00`) : undefined
        }
        onConfirmTime={(time) => {
          setStartTimeDate(time);
          startTimeModalizeRef.current?.close();
        }}
      />
    </ModalScreenLayout>
  );
}
